import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useShop } from "../contexts/ShopContext";
import { useTracking } from "../contexts/TrackingContext";
import { useI18n } from "../contexts/I18nContext";
import ReactMarkdown from "react-markdown";
import api from "../api";
import SEO from "../components/SEO";
import ProductCard from "../components/ProductCard";
import HorizontalScroll from "../components/HorizontalScroll";
import { CustomerAccountHome, CustomerOrders, CustomerProfile } from "../components/CustomerAccountPanels";
import { formatDate } from "../utils/date";
import { isAdminRole, roleLabel } from "../utils/roles";

/* ── SVG Icon Components ── */
const Icons = {
  home: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  ),
  ai: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  ),
  quote: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  ),
  inquiry: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  ),
  cart: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121 0 2.09-.773 2.34-1.865l1.84-8.058A1.125 1.125 0 0020.053 3H5.106m2.394 11.25l-1-.818" />
    </svg>
  ),
  heart: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  ),
  building: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  ),
  chat: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
    </svg>
  ),
  user: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  ),
  product: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m0 0a48.44 48.44 0 0110.5 0m-10.5 0V4.875c0-.621.504-1.125 1.125-1.125h8.25c.621 0 1.125.504 1.125 1.125v2.234" />
    </svg>
  ),
  rental: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z" />
    </svg>
  ),
  logout: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
    </svg>
  ),
  compare: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
    </svg>
  ),
  sitemap: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25a2.25 2.25 0 01-2.25-2.25v-2.25z" />
    </svg>
  ),
  store: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  ),
  search: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  ),
};

const MarkdownLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#0071e3] hover:underline">{children}</a>
);

/* ═══════════════════════════════════════════════════════════
   고객용 ERP – Customer ERP Dashboard
   ═══════════════════════════════════════════════════════════ */
export default function CustomerERPPage() {
  const { user, loading: authLoading, logout, updateProfile } = useAuth();
  const { cartCount } = useShop();
  const tracking = useTracking();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the source of truth, including browser back/forward navigation.
  const validSections = ["home", "ai", "copier", "quotes", "newquote", "inquiry", "cart", "orders", "corporate", "trip", "history", "profile", "community"];
  const requestedSection = searchParams.get("section") || searchParams.get("tab") || "home";
  const section = validSections.includes(requestedSection) ? requestedSection : "home";
  const setSection = key => setSearchParams({ section: key });
  useEffect(() => {
    if (section === "cart") navigate("/cart", { replace: true });
  }, [section, navigate]);
  useEffect(() => {
    if (!authLoading && !user) {
      const query = searchParams.toString();
      navigate("/login", { replace: true, state: { from: `/my${query ? `?${query}` : ""}` } });
    }
  }, [authLoading, user, navigate, searchParams]);

  // Data
  const [quotes, setQuotes] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");
  const orderRequest = useRef(0);
  const userId = user?.id;
  const fetchOrders = useCallback(async () => {
    if (!userId) return;
    const request = ++orderRequest.current;
    setOrdersLoading(true); setOrdersError("");
    try {
      const { data } = await api.get("/api/orders");
      if (!Array.isArray(data)) throw new Error("Invalid orders response");
      if (orderRequest.current === request) setOrders(data);
    } catch (error) {
      if (orderRequest.current === request) setOrdersError(error.response?.data?.error || "주문 내역을 불러오지 못했습니다. 다시 시도해 주세요.");
    } finally {
      if (orderRequest.current === request) setOrdersLoading(false);
    }
  }, [userId]);
  useEffect(() => {
    if (!authLoading && userId) fetchOrders();
    return () => { orderRequest.current += 1; };
  }, [authLoading, userId, fetchOrders]);

  // Forms
  const [profileForm, setProfileForm] = useState({ name: "", company: "", phone: "" });
  const [inquiryForm, setInquiryForm] = useState({ inquiry_type: "rental", message: "", company: "", phone: "" });
  const [quoteForm, setQuoteForm] = useState({ title: t.erp_quotes || "견적서", company: "", items: [], note: "" });
  const [newItem, setNewItem] = useState({ product_name: "", qty: 1, type: "purchase", price_note: "" });
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("info"); // info, success, error

  // AI Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);

  // Corporate features
  const [bulkItems, setBulkItems] = useState([{ product: "", qty: 1, location: "" }]);

  // Account deletion
  const [deleteStep, setDeleteStep] = useState(0); // 0=hidden, 1=confirm
  const [deleteInput, setDeleteInput] = useState("");
  const [deleteMsg, setDeleteMsg] = useState("");

  // Copier report
  const [copierReports, setCopierReports] = useState([]);
  const [copierUploading, setCopierUploading] = useState(false);
  const [copierAnalyzing, setCopierAnalyzing] = useState(false);
  const [copierDetail, setCopierDetail] = useState(null);
  const [copierClientName, setCopierClientName] = useState("");
  const [copierFile, setCopierFile] = useState(null);
  const [copierUploadedId, setCopierUploadedId] = useState(null);

  useEffect(() => {
    if (authLoading || !user) return;
    setProfileForm({ name: user.name || "", company: user.company || "", phone: user.phone || "" });
    setInquiryForm(f => ({ ...f, company: user.company || "", phone: user.phone || "" }));
    setQuoteForm(f => ({ ...f, company: user.company || "" }));

    // Fetch all data in parallel
    Promise.all([
      api.get("/api/user/quotes").catch(() => ({ data: [] })),
      api.get("/api/user/chat-sessions").catch(() => ({ data: [] })),
      api.get("/api/user/inquiries").catch(() => ({ data: [] })),
      api.get("/api/copier-reports").catch(() => ({ data: [] })),
    ]).then(([qRes, csRes, inqRes, crRes]) => {
      setQuotes(qRes.data);
      setChatSessions(csRes.data);
      setInquiries(inqRes.data || []);
      setCopierReports(crRes.data || []);
    });
  }, [user, authLoading, navigate]);

  const showMsg = (text, type = "info") => {
    setMsg(text);
    setMsgType(type);
    setTimeout(() => setMsg(""), 4000);
  };

  // ── HANDLERS ──
  const handleQuickInquiry = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/api/user/quick-inquiry", inquiryForm);
      showMsg(data.message, "success");
      setInquiryForm(f => ({ ...f, message: "" }));
      // Refresh inquiries
      api.get("/api/user/inquiries").then(r => setInquiries(r.data || [])).catch(() => {});
    } catch (err) { showMsg(err.response?.data?.error || "오류 발생", "error"); }
  };

  const addQuoteItem = () => {
    if (!newItem.product_name) return;
    setQuoteForm(f => ({ ...f, items: [...f.items, { ...newItem }] }));
    setNewItem({ product_name: "", qty: 1, type: "purchase", price_note: "" });
  };

  const removeQuoteItem = (idx) => {
    setQuoteForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    if (quoteForm.items.length === 0) { showMsg("항목을 추가해주세요", "error"); return; }
    try {
      const { data } = await api.post("/api/user/quotes", quoteForm);
      showMsg(data.message, "success");
      setQuoteForm({ title: t.erp_quotes || "견적서", company: user.company || "", items: [], note: "" });
      const res = await api.get("/api/user/quotes");
      setQuotes(res.data);
      setSection("quotes");
    } catch (err) { showMsg(err.response?.data?.error || "오류 발생", "error"); }
  };

  const submitQuote = async (id) => {
    try {
      const { data } = await api.post(`/api/user/quotes/${id}/submit`);
      showMsg(data.message, "success");
      const res = await api.get("/api/user/quotes");
      setQuotes(res.data);
    } catch (err) { showMsg(err.response?.data?.error || "오류", "error"); }
  };

  const deleteQuote = async (id) => {
    try {
      await api.delete(`/api/user/quotes/${id}`);
      setQuotes(q => q.filter(x => x.id !== id));
      showMsg("견적서가 삭제되었습니다", "success");
    } catch {}
  };

  // AI Chat handler
  const sendChatMessage = async (e) => {
    e.preventDefault();
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: msg }]);
    setChatLoading(true);
    try {
      const { data } = await api.post("/api/chat", {
        message: msg,
        session_id: chatSessionId,
        visitor_id: tracking?.visitorId || "",
      });
      setChatSessionId(data.session_id);
      setChatMessages(prev => [...prev, {
        role: "assistant", content: data.answer,
        copiers: data.recommended_copiers || [],
        toners: data.recommended_toners || [],
      }]);
    } catch {
      setChatMessages(prev => [...prev, {
        role: "assistant",
        content: t.erp_chatError || "죄송합니다, 오류가 발생했습니다. 다시 시도해주세요.",
      }]);
    } finally { setChatLoading(false); }
  };

  // Load a past chat session
  const loadChatSession = async (sessionId) => {
    try {
      const { data } = await api.get(`/api/user/chat-sessions/${encodeURIComponent(sessionId)}/messages`);
      setChatSessionId(sessionId);
      setChatMessages(data.map(m => {
        let copiers = [], toners = [];
        if (m.recommended_products) {
          try {
            const rp = typeof m.recommended_products === "string" ? JSON.parse(m.recommended_products) : m.recommended_products;
            copiers = rp.copiers || [];
            toners = rp.toners || [];
          } catch {}
        }
        return { role: m.role, content: m.message, copiers, toners };
      }));
      setSection("ai");
    } catch {
      showMsg("상담 내역을 불러올 수 없습니다", "error");
    }
  };

  // Start a brand-new chat
  const startNewChat = () => {
    setChatSessionId(null);
    setChatMessages([]);
    setChatInput("");
  };

  // Bulk inquiry for corporate
  const handleBulkInquiry = async (e) => {
    e.preventDefault();
    const validItems = bulkItems.filter(b => b.product.trim());
    if (validItems.length === 0) { showMsg("제품 정보를 입력해주세요", "error"); return; }
    const itemsText = validItems.map((b, i) => `${i + 1}. ${b.product} × ${b.qty}대 (설치: ${b.location || "미정"})`).join("\n");
    const message = `[대량 구매/렌탈 문의]\n\n${itemsText}\n\n회사: ${user.company || profileForm.company}\n담당자: ${user.name}\n연락처: ${user.phone || profileForm.phone}`;
    try {
      await api.post("/api/user/quick-inquiry", { inquiry_type: "purchase", message, company: user.company || profileForm.company, phone: user.phone || profileForm.phone });
      showMsg("대량 문의가 접수되었습니다!", "success");
      setBulkItems([{ product: "", qty: 1, location: "" }]);
      api.get("/api/user/inquiries").then(r => setInquiries(r.data || [])).catch(() => {});
    } catch (err) { showMsg(err.response?.data?.error || "문의 실패", "error"); }
  };

  if (authLoading) return <div className="customer-account customer-account__main" role="status">회원정보를 확인하는 중입니다.</div>;
  if (!user) return null;

  // ── Navigation items ──
  const navItems = [
    { key: "home", label: t.erp_home, icon: Icons.home },
    { key: "ai", label: t.erp_aiConsult, icon: Icons.ai },
    { key: "copier", label: t.erp_usageAnalysis, icon: Icons.product },
    { key: "quotes", label: t.erp_quoteManage, icon: Icons.quote },
    { key: "inquiry", label: t.erp_inquiryManage, icon: Icons.inquiry },
    { key: "cart", label: t.erp_cart, icon: Icons.cart, badge: cartCount },
    { key: "orders", label: t.erp_orderHistory, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"/></svg>, badge: orders.filter(o => o.status !== "delivered" && o.status !== "cancelled" && o.status !== "refunded").length || 0 },
    { key: "corporate", label: t.erp_purchaseManage, icon: Icons.building },
    { key: "trip", label: t.erp_tripManage || '출장 관리', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.988-1.106a48.554 48.554 0 00-10.024 0 1.106 1.106 0 00-.988 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg> },
    { key: "history", label: t.erp_chatHistory, icon: Icons.chat },
    { key: "profile", label: t.erp_myInfo, icon: Icons.user },
    { key: "community", label: t.erp_reviewLounge, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" /></svg> },
  ];

  const statusLabel = { draft: t.erp_statusDraft, submitted: t.erp_statusSubmitted, reviewed: t.erp_statusReviewed, sent: t.erp_statusSent };
  const statusColor = { draft: "bg-gray-100 text-gray-600", submitted: "bg-blue-100 text-blue-700", reviewed: "bg-yellow-100 text-yellow-700", sent: "bg-green-100 text-green-700" };
  const inquiryTypeLabel = { rental: t.erp_typeRental, purchase: t.erp_typePurchase, toner: t.erp_typeToner, etc: t.erp_typeEtc };

  return (
    <>
      <SEO title={`${user.name} ${t.erp_myPage}`} description={t.erp_seoDesc} path="/my" noindex />

      <div className="customer-account">
        <header className="customer-account__header">
          <span className="customer-account__eyebrow">ILKWANG OFFICE</span>
          <div><div><h1>마이페이지</h1><p>{user.name || "고객"}님, 안녕하세요.</p><p>{user.company || user.email}</p></div><button type="button" onClick={() => { logout(); navigate("/"); }}>로그아웃</button></div>
        </header>
        <nav className="customer-account__tabs" aria-label="마이페이지 메뉴">
          <button type="button" aria-current={section === "home" ? "page" : undefined} onClick={() => setSection("home")}>마이홈</button>
          <button type="button" aria-current={section === "orders" ? "page" : undefined} onClick={() => setSection("orders")}>주문·배송</button>
          <Link to="/cart">장바구니{cartCount ? ` (${cartCount})` : ""}</Link>
          <button type="button" aria-current={section === "profile" ? "page" : undefined} onClick={() => setSection("profile")}>회원정보</button>
        </nav>
        <details className="customer-account__extras">
          <summary>견적·문의·기타 서비스</summary>
          <nav className="customer-account__extra-links" aria-label="추가 고객 서비스">{navItems.filter(item => !["home", "orders", "cart", "profile"].includes(item.key)).map(item => <button type="button" key={item.key} aria-current={section === item.key ? "page" : undefined} onClick={() => setSection(item.key)}>{item.label}</button>)}</nav>
        </details>
        {msg && <div className={`customer-account__main ${msgType === "error" ? "customer-account__error" : "customer-account__success"}`} role={msgType === "error" ? "alert" : "status"}>{msg}</div>}
        <div>
          <main className="customer-account__main">

            {/* ═══ HOME SECTION ═══ */}
            {section === "home" && <CustomerAccountHome user={user} cartCount={cartCount} orders={orders} loading={ordersLoading} error={ordersError} onReload={fetchOrders} onSection={setSection} />}

            {/* ═══ AI CHAT SECTION ═══ */}
            {section === "ai" && (
              <div className="animate-fade-in max-w-3xl mx-auto">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-[20px] font-bold text-[#1d1d1f] mb-1">{t.erp_aiConsult}</h2>
                    <p className="text-[13px] text-gray-500">{t.erp_aiDesc}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {chatMessages.length > 0 && (
                      <button onClick={startNewChat}
                        className="flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-[#0071e3] border border-gray-200 rounded-lg px-3 py-1.5 transition">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                        {t.erp_newChat}
                      </button>
                    )}
                    <button onClick={() => setSection("history")}
                      className="flex items-center gap-1.5 text-[12px] text-[#0071e3] hover:text-blue-700 border border-[#0071e3]/30 rounded-lg px-3 py-1.5 transition">
                      {Icons.chat}
                      <span>{t.erp_prevChats} ({chatSessions.length})</span>
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 overflow-hidden flex flex-col" style={{ height: "calc(100vh - 280px)", minHeight: "clamp(320px, calc(100vh - 280px), 480px)" }}>
                  {/* Chat Messages */}
                  <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    {chatMessages.length === 0 && (
                      <div className="text-center py-16">
                        <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                          </svg>
                        </div>
                        <h3 className="font-bold text-[17px] text-[#1d1d1f] mb-1">{t.erp_aiAsk}</h3>
                        <p className="text-[13px] text-gray-500 mb-6">{t.erp_aiHint}</p>
                        <div className="flex flex-wrap justify-center gap-2">
                          {[t.erp_suggestOffice, t.erp_suggestCompare, t.erp_suggestToner, t.erp_suggestSmall].map((q, i) => (
                            <button key={i} onClick={() => setChatInput(q)}
                              className="text-[12px] bg-[#f5f5f7] text-gray-600 rounded-full px-4 py-2 hover:bg-gray-200 transition">
                              {q}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {chatMessages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                          m.role === "user" ? "bg-[#0071e3] text-white" : "bg-[#f5f5f7] text-[#1d1d1f]"
                        }`}>
                          {m.role === "assistant" ? (
                            <div className="chat-markdown text-[13px] leading-relaxed">
                              <ReactMarkdown components={{ a: MarkdownLink }}>{m.content}</ReactMarkdown>
                            </div>
                          ) : (
                            <p className="text-[13px]">{m.content}</p>
                          )}
                          {m.copiers?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200/50">
                              <p className="text-[11px] font-medium text-gray-500 mb-2">{t.erp_recCopier}</p>
                              <div className="space-y-2">
                                {m.copiers.map(c => <ProductCard key={c.id} product={c} type="copier" compact />)}
                              </div>
                            </div>
                          )}
                          {m.toners?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200/50">
                              <p className="text-[11px] font-medium text-gray-500 mb-2">{t.erp_recToner}</p>
                              <div className="space-y-2">
                                {m.toners.map(t => <ProductCard key={t.id} product={t} type="toner" compact />)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex justify-start">
                        <div className="bg-[#f5f5f7] rounded-2xl px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Chat Input */}
                  <form onSubmit={sendChatMessage} className="border-t border-gray-100 px-4 py-3 flex gap-2">
                    <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)}
                      placeholder={t.erp_msgPlaceholder} disabled={chatLoading}
                      className="flex-1 bg-[#f5f5f7] rounded-full px-4 py-2.5 text-[13px] text-[#1d1d1f] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0071e3]/20 disabled:opacity-50 transition" />
                    <button type="submit" disabled={chatLoading || !chatInput.trim()}
                      className="bg-[#0071e3] text-white w-9 h-9 rounded-full flex items-center justify-center hover:bg-blue-700 disabled:opacity-30 transition shrink-0 self-center">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
                      </svg>
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* ═══ QUOTES SECTION ═══ */}
            {section === "quotes" && (
              <div className="animate-fade-in">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-[20px] font-bold text-[#1d1d1f]">{t.erp_quoteManage}</h2>
                    <p className="text-[13px] text-gray-500">{t.erp_quoteManageDesc}</p>
                  </div>
                  <button onClick={() => setSection("newquote")}
                    className="flex items-center gap-1.5 bg-[#0071e3] text-white px-4 py-2.5 rounded-xl text-[13px] font-semibold hover:bg-blue-700 transition shadow-sm">
                    {Icons.quote}
                    <span>{t.erp_newQuote}</span>
                  </button>
                </div>

                {quotes.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-16 text-center">
                    <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-600">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                    </div>
                    <p className="text-[15px] font-semibold text-[#1d1d1f] mb-1">{t.erp_noQuotes}</p>
                    <p className="text-[13px] text-gray-500 mb-4">{t.erp_noQuotesHint}</p>
                    <button onClick={() => setSection("newquote")}
                      className="text-[13px] text-[#0071e3] font-semibold hover:underline">{t.erp_createQuote}</button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {quotes.map(q => (
                      <div key={q.id} className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-5 hover:shadow-md transition">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[15px] font-bold text-[#1d1d1f]">{q.title}</span>
                              <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${statusColor[q.status] || ""}`}>
                                {statusLabel[q.status]}
                              </span>
                            </div>
                            <p className="text-[12px] text-gray-500">
                              {q.company ? `${q.company} · ` : ""}{Array.isArray(q.items) ? q.items.length : 0} {t.erp_items}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-0.5">{formatDate(q.created_at)}</p>
                            {q.admin_note && (
                              <div className="mt-2 bg-blue-50 rounded-lg px-3 py-2">
                                <p className="text-[11px] text-blue-700">{t.erp_adminNote} {q.admin_note}</p>
                              </div>
                            )}
                            {Array.isArray(q.items) && q.items.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {q.items.map((item, idx) => (
                                  <span key={idx} className="inline-flex items-center bg-gray-100 rounded-lg px-2 py-1 text-[11px] text-gray-600">
                                    {item.product_name} ×{item.qty}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 ml-3 shrink-0">
                            {q.status === "draft" && (
                              <button onClick={() => submitQuote(q.id)}
                                className="text-[12px] bg-[#0071e3] text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition font-medium">
                                {t.erp_submit}
                              </button>
                            )}
                            <button onClick={() => deleteQuote(q.id)}
                              className="text-[12px] bg-red-50 text-red-500 px-3 py-2 rounded-xl hover:bg-red-100 transition">
                              {t.erp_delete}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ═══ NEW QUOTE SECTION ═══ */}
            {section === "newquote" && (
              <div className="animate-fade-in max-w-2xl">
                <button onClick={() => setSection("quotes")} className="text-[13px] text-[#0071e3] hover:underline mb-4 flex items-center gap-1">
                  {t.erp_backToQuotes}
                </button>
                <h2 className="text-[20px] font-bold text-[#1d1d1f] mb-5">{t.erp_newQuoteTitle}</h2>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-6">
                  <form onSubmit={handleCreateQuote} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[12px] font-semibold text-gray-500 mb-1.5">{t.erp_quoteTitle}</label>
                        <input value={quoteForm.title} onChange={e => setQuoteForm(f => ({ ...f, title: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] focus:ring-2 focus:ring-[#0071e3]/20 focus:border-[#0071e3] outline-none transition" />
                      </div>
                      <div>
                        <label className="block text-[12px] font-semibold text-gray-500 mb-1.5">{t.erp_companyName}</label>
                        <input value={quoteForm.company} onChange={e => setQuoteForm(f => ({ ...f, company: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] focus:ring-2 focus:ring-[#0071e3]/20 focus:border-[#0071e3] outline-none transition" />
                      </div>
                    </div>

                    {/* Add Item */}
                    <div className="bg-[#f5f5f7] rounded-xl p-4">
                      <h3 className="text-[13px] font-semibold text-[#1d1d1f] mb-3">{t.erp_addItem}</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <input placeholder={t.erp_productModel} value={newItem.product_name}
                          onChange={e => setNewItem(n => ({ ...n, product_name: e.target.value }))}
                          className="border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] bg-white focus:ring-2 focus:ring-[#0071e3]/20 outline-none" />
                        <input type="number" min="1" placeholder={t.erp_quantity} value={newItem.qty}
                          onChange={e => setNewItem(n => ({ ...n, qty: parseInt(e.target.value) || 1 }))}
                          className="border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] bg-white focus:ring-2 focus:ring-[#0071e3]/20 outline-none" />
                        <select value={newItem.type} onChange={e => setNewItem(n => ({ ...n, type: e.target.value }))}
                          className="border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] bg-white focus:ring-2 focus:ring-[#0071e3]/20 outline-none">
                          <option value="purchase">{t.erp_typePurchase}</option>
                          <option value="rental">{t.erp_typeRental}</option>
                          <option value="toner">{t.erp_typeToner}</option>
                        </select>
                        <button type="button" onClick={addQuoteItem}
                          className="bg-[#0071e3] text-white rounded-xl text-[13px] font-medium hover:bg-blue-700 transition py-2.5">
                          + {t.erp_addMore}
                        </button>
                      </div>
                    </div>

                    {/* Items list */}
                    {quoteForm.items.length > 0 && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <table className="w-full text-[13px]">
                          <thead className="bg-[#f5f5f7]">
                            <tr>
                              <th className="text-left px-4 py-2.5 font-semibold text-gray-500">{t.erp_productNameCol}</th>
                              <th className="text-center px-2 py-2.5 font-semibold text-gray-500">{t.erp_quantity}</th>
                              <th className="text-center px-2 py-2.5 font-semibold text-gray-500">{t.erp_typeCol}</th>
                              <th className="w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {quoteForm.items.map((item, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="px-4 py-2.5 text-[#1d1d1f]">{item.product_name}</td>
                                <td className="text-center px-2 py-2.5">{item.qty}</td>
                                <td className="text-center px-2 py-2.5">{item.type === "rental" ? t.erp_typeRental : item.type === "toner" ? t.erp_typeToner : t.erp_typePurchase}</td>
                                <td className="px-2 py-2.5">
                                  <button type="button" onClick={() => removeQuoteItem(i)} className="text-red-400 hover:text-red-600">✕</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <textarea placeholder={t.erp_additionalNotes} value={quoteForm.note}
                      onChange={e => setQuoteForm(f => ({ ...f, note: e.target.value }))}
                      rows={3} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[13px] focus:ring-2 focus:ring-[#0071e3]/20 focus:border-[#0071e3] outline-none transition" />

                    <button type="submit"
                      className="w-full bg-[#0071e3] text-white py-3 rounded-xl text-[15px] font-bold hover:bg-blue-700 transition shadow-sm">
                      {t.erp_saveQuote}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* ═══ INQUIRY SECTION ═══ */}
            {section === "inquiry" && (
              <div className="animate-fade-in">
                <h2 className="text-[20px] font-bold text-[#1d1d1f] mb-1">{t.erp_inquiryManage}</h2>
                <p className="text-[13px] text-gray-500 mb-5">{t.erp_inquiryManageDesc}</p>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  {/* Quick Inquiry Form */}
                  <div className="lg:col-span-2">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-6">
                      <h3 className="text-[15px] font-bold text-[#1d1d1f] mb-1">{t.erp_quickInquiry}</h3>
                      <p className="text-[12px] text-gray-500 mb-4">{t.erp_autoFillInfo}</p>

                      <div className="bg-[#f5f5f7] rounded-xl p-3 mb-4">
                        <p className="text-[13px] font-semibold text-[#1d1d1f]">{user.name}</p>
                        <p className="text-[11px] text-gray-500">{user.company ? `${user.company} · ` : ""}{user.email}</p>
                      </div>

                      <form onSubmit={handleQuickInquiry} className="space-y-3">
                        <select value={inquiryForm.inquiry_type} onChange={e => setInquiryForm(f => ({ ...f, inquiry_type: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] focus:ring-2 focus:ring-[#0071e3]/20 outline-none">
                          <option value="rental">{t.erp_rentalInquiry}</option>
                          <option value="purchase">{t.erp_purchaseInquiry}</option>
                          <option value="toner">{t.erp_tonerInquiry}</option>
                          <option value="etc">{t.erp_etcInquiry}</option>
                        </select>
                        <textarea value={inquiryForm.message} onChange={e => setInquiryForm(f => ({ ...f, message: e.target.value }))}
                          rows={4} required placeholder={t.erp_inquiryPlaceholder}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[13px] focus:ring-2 focus:ring-[#0071e3]/20 outline-none transition resize-none" />
                        <button type="submit"
                          className="w-full bg-[#0071e3] text-white py-2.5 rounded-xl text-[13px] font-bold hover:bg-blue-700 transition">
                          {t.erp_sendInquiry}
                        </button>
                      </form>
                    </div>
                  </div>

                  {/* Inquiry History */}
                  <div className="lg:col-span-3">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-6">
                      <h3 className="text-[15px] font-bold text-[#1d1d1f] mb-4">{t.erp_inquiryHistory}</h3>
                      {inquiries.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                          <span className="block mb-2 text-orange-500"><svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg></span>
                          <p className="text-[13px]">{t.erp_noInquiries}</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                          {inquiries.map(inq => (
                            <div key={inq.id} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50/50 transition">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  inq.status === "resolved" ? "bg-green-100 text-green-700" :
                                  inq.status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                                  "bg-blue-100 text-blue-700"
                                }`}>
                                  {inq.status === "resolved" ? t.erp_answered : inq.status === "in_progress" ? t.erp_processing : t.erp_received}
                                </span>
                                <span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                  {inquiryTypeLabel[inq.inquiry_type] || inq.inquiry_type}
                                </span>
                                <span className="text-[11px] text-gray-400 ml-auto">
                                  {formatDate(inq.created_at)}
                                </span>
                              </div>
                              <p className="text-[13px] text-[#1d1d1f] whitespace-pre-wrap">{inq.message}</p>
                              {inq.admin_reply && (
                                <div className="mt-2 bg-blue-50 rounded-lg p-3">
                                  <p className="text-[11px] text-blue-600 font-medium mb-1">{t.erp_adminReply}</p>
                                  <p className="text-[12px] text-blue-800">{inq.admin_reply}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* One cart implementation keeps quantity, delivery fees and payment in sync. */}
            {section === "cart" && <Link className="customer-account__button" to="/cart">장바구니 열기</Link>}

            {/* ═══ COPIER REPORT SECTION ═══ */}
            {section === "copier" && (
              <div className="animate-fade-in">
                <h2 className="text-[20px] font-bold text-[#1d1d1f] mb-1">{t.erp_copierAnalysis}</h2>
                <p className="text-[13px] text-gray-500 mb-5">{t.erp_copierAnalysisDesc}</p>

                {/* Upload Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-6 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-blue-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/></svg>
                    </div>
                    <h3 className="font-bold text-[15px] text-[#1d1d1f]">{t.erp_uploadReport}</h3>
                  </div>
                  <p className="text-[12px] text-gray-500 mb-4">{t.erp_uploadReportDesc}</p>

                  <div className="mb-3">
                    <label className="block text-[12px] font-medium text-gray-700 mb-1">{t.erp_clientName}</label>
                    <input type="text" value={copierClientName} onChange={e => setCopierClientName(e.target.value)}
                      placeholder={t.erp_clientExample} className="w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                  </div>

                  <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-2xl cursor-pointer transition ${copierFile ? "border-blue-400 bg-blue-50/60" : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/30"}`}>
                    {copierFile ? (
                      <div className="flex flex-col items-center gap-1">
                        <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                        <span className="text-[13px] font-medium text-blue-700 truncate max-w-[250px]">{copierFile.name}</span>
                        <span className="text-[12px] text-gray-400">{t.erp_selectOtherFile}</span>
                      </div>
                    ) : (
                      <>
                        <svg className="w-8 h-8 text-gray-300 mb-1" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a2.25 2.25 0 002.25-2.25V5.25a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v13.5a2.25 2.25 0 002.25 2.25z"/></svg>
                        <span className="text-[12px] text-gray-400">{t.erp_clickToSelect}</span>
                      </>
                    )}
                    <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.pdf,.bmp,.webp"
                      disabled={copierUploading || copierAnalyzing}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setCopierFile(file);
                        setCopierUploadedId(null);
                        setCopierDetail(null);
                      }} />
                  </label>

                  {/* 분석 시작 버튼 */}
                  {copierFile && !copierAnalyzing && (
                    <button
                      disabled={copierUploading}
                      onClick={async () => {
                        setCopierUploading(true);
                        setCopierAnalyzing(true);
                        try {
                          // Step 1: Upload file
                          const fd = new FormData();
                          fd.append("file", copierFile);
                          if (copierClientName) fd.append("client_name", copierClientName);
                          const uploadRes = await api.post("/api/copier-reports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          const reportId = uploadRes.data.id;
                          setCopierUploadedId(reportId);
                          showMsg("파일 업로드 완료! 분석 중...", "success");

                          // Step 2: Analyze
                          const analyzeRes = await api.post(`/api/copier-reports/${reportId}/analyze`);
                          setCopierDetail(analyzeRes.data.parsed);
                          showMsg("리포트 분석 완료!", "success");
                          api.get("/api/copier-reports").then(r => setCopierReports(r.data || [])).catch(() => {});
                        } catch (err) {
                          showMsg(err.response?.data?.error || "분석 실패", "error");
                        } finally {
                          setCopierUploading(false);
                          setCopierAnalyzing(false);
                          setCopierFile(null);
                        }
                      }}
                      className="w-full mt-3 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold text-[14px] shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
                    >
                      🔍 {t.erp_startAnalysis}
                    </button>
                  )}
                  {copierAnalyzing && (
                    <div className="flex items-center justify-center gap-2 mt-3 py-3 text-blue-600">
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                      <span className="text-sm font-medium">{t.erp_aiAnalyzing}</span>
                    </div>
                  )}
                </div>

                {/* Recently analyzed result */}
                {copierDetail && (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-4 sm:p-6 mb-6 overflow-hidden">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-[15px] text-[#1d1d1f]">{t.erp_analysisResult}</h3>
                      <button onClick={() => setCopierDetail(null)} className="text-[12px] text-gray-400 hover:text-gray-600">{t.erp_close}</button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                      <div className="bg-white/80 rounded-xl p-3">
                        <div className="text-[11px] text-gray-500">{t.erp_modelName}</div>
                        <div className="text-[13px] font-bold text-[#1d1d1f] truncate">{copierDetail.copier_model || "-"}</div>
                      </div>
                      <div className="bg-white/80 rounded-xl p-3">
                        <div className="text-[11px] text-gray-500">{t.erp_reportDate}</div>
                        <div className="text-[13px] font-bold text-[#1d1d1f]">{copierDetail.report_date || "-"}</div>
                      </div>
                      <div className="bg-white/80 rounded-xl p-3">
                        <div className="text-[11px] text-gray-500">{t.erp_totalPages}</div>
                        <div className="text-[13px] font-bold text-blue-600">{copierDetail.total_pages?.toLocaleString() || 0}</div>
                      </div>
                      <div className="bg-white/80 rounded-xl p-3">
                        <div className="text-[11px] text-gray-500">{t.erp_color}</div>
                        <div className="text-[13px] font-bold text-purple-600">{copierDetail.color_pages?.toLocaleString() || 0}</div>
                      </div>
                      <div className="bg-white/80 rounded-xl p-3">
                        <div className="text-[11px] text-gray-500">{t.erp_bw}</div>
                        <div className="text-[13px] font-bold text-gray-700">{copierDetail.bw_pages?.toLocaleString() || 0}</div>
                      </div>
                      {copierDetail.scan_pages > 0 && (
                        <div className="bg-white/80 rounded-xl p-3">
                          <div className="text-[11px] text-gray-500">{t.erp_scan}</div>
                          <div className="text-[13px] font-bold text-teal-600">{copierDetail.scan_pages?.toLocaleString()}</div>
                        </div>
                      )}
                      {copierDetail.fax_pages > 0 && (
                        <div className="bg-white/80 rounded-xl p-3">
                          <div className="text-[11px] text-gray-500">{t.erp_fax}</div>
                          <div className="text-[13px] font-bold text-orange-600">{copierDetail.fax_pages?.toLocaleString()}</div>
                        </div>
                      )}
                      {copierDetail.copy_pages > 0 && (
                        <div className="bg-white/80 rounded-xl p-3">
                          <div className="text-[11px] text-gray-500">{t.erp_copy}</div>
                          <div className="text-[13px] font-bold text-indigo-600">{copierDetail.copy_pages?.toLocaleString()}</div>
                        </div>
                      )}
                      {copierDetail.print_pages > 0 && (
                        <div className="bg-white/80 rounded-xl p-3">
                          <div className="text-[11px] text-gray-500">{t.erp_printPages}</div>
                          <div className="text-[13px] font-bold text-sky-600">{copierDetail.print_pages?.toLocaleString()}</div>
                        </div>
                      )}
                      {copierDetail.a3_pages > 0 && (
                        <div className="bg-white/80 rounded-xl p-3">
                          <div className="text-[11px] text-gray-500">A3</div>
                          <div className="text-[13px] font-bold text-amber-600">{copierDetail.a3_pages?.toLocaleString()}</div>
                        </div>
                      )}
                    </div>

                    {/* Toner Levels */}
                    {(copierDetail.toner_c_pct != null || copierDetail.toner_k_pct != null) && (
                      <div className="mb-4">
                        <div className="text-[12px] font-semibold text-gray-600 mb-2">{t.erp_tonerRemaining}</div>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { key: "toner_c_pct", label: "C", color: "bg-cyan-400" },
                            { key: "toner_m_pct", label: "M", color: "bg-pink-400" },
                            { key: "toner_y_pct", label: "Y", color: "bg-yellow-400" },
                            { key: "toner_k_pct", label: "K", color: "bg-gray-700" },
                          ].map(t => (
                            <div key={t.key} className="bg-white/80 rounded-xl p-2 text-center">
                              <div className="text-[11px] text-gray-500 mb-1">{t.label}</div>
                              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${t.color}`}
                                  style={{ width: `${copierDetail[t.key] ?? 0}%` }} />
                              </div>
                              <div className="text-[12px] font-bold mt-1">{copierDetail[t.key] ?? "-"}%</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* User Details */}
                    {copierDetail.users?.length > 0 && (
                      <div>
                        <div className="text-[12px] font-semibold text-gray-600 mb-2">{t.erp_usageByUser}</div>
                        <HorizontalScroll label="사용자별 복합기 사용량 표">
                        <div className="bg-white/80 rounded-xl overflow-x-auto">
                          <table className="w-full text-[12px] min-w-[320px]">
                            <thead className="bg-gray-50/50">
                              <tr>
                                <th className="text-left px-2 sm:px-3 py-2 text-gray-500 font-medium whitespace-nowrap">{t.erp_user}</th>
                                <th className="text-right px-2 sm:px-3 py-2 text-gray-500 font-medium whitespace-nowrap">{t.erp_color}</th>
                                <th className="text-right px-2 sm:px-3 py-2 text-gray-500 font-medium whitespace-nowrap">{t.erp_bw}</th>
                                <th className="text-right px-2 sm:px-3 py-2 text-gray-500 font-medium whitespace-nowrap">{t.erp_total}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {copierDetail.users.map((u, i) => (
                                <tr key={i} className="border-t border-gray-100">
                                  <td className="px-2 sm:px-3 py-2 text-[#1d1d1f] font-medium max-w-[100px] sm:max-w-[150px] truncate" title={u.name}>{u.name}</td>
                                  <td className="px-2 sm:px-3 py-2 text-right text-purple-600 whitespace-nowrap">{u.color_pages?.toLocaleString() || 0}</td>
                                  <td className="px-2 sm:px-3 py-2 text-right text-gray-600 whitespace-nowrap">{u.bw_pages?.toLocaleString() || 0}</td>
                                  <td className="px-2 sm:px-3 py-2 text-right font-bold text-[#1d1d1f] whitespace-nowrap">{u.total_pages?.toLocaleString() || 0}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        </HorizontalScroll>
                      </div>
                    )}
                    {copierDetail.extra_info && (
                      <div className="mt-3 bg-white/80 rounded-xl p-3">
                        <div className="text-[11px] text-gray-500 mb-1">{t.erp_extraInfo}</div>
                        <div className="text-[12px] text-[#1d1d1f]">{copierDetail.extra_info}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Report History */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-6">
                  <h3 className="font-bold text-[15px] text-[#1d1d1f] mb-4">{t.erp_analysisHistory} ({copierReports.length}{t.erp_countUnit})</h3>
                  {copierReports.length === 0 ? (
                    <p className="text-[13px] text-gray-400 text-center py-6">{t.erp_noReports}</p>
                  ) : (
                    <div className="space-y-3">
                      {copierReports.map(r => (
                        <div key={r.id} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50/50 transition cursor-pointer"
                          onClick={async () => {
                            try {
                              const { data } = await api.get(`/api/copier-reports/${r.id}`);
                              setCopierDetail(data.parsed_data || data);
                            } catch {}
                          }}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[13px] font-bold text-[#1d1d1f]">{r.copier_model || t.erp_unknownModel}</span>
                            <span className="text-[11px] text-gray-400">{r.report_date || formatDate(r.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-4 text-[12px] text-gray-500">
                            {r.client_name && <span>{t.erp_client}: {r.client_name}</span>}
                            <span>{t.erp_totalPrefix} {(r.total_pages || 0).toLocaleString()}{t.erp_sheets}</span>
                            <span className="text-purple-600">{t.erp_color} {(r.color_pages || 0).toLocaleString()}</span>
                            <span>{t.erp_bw} {(r.bw_pages || 0).toLocaleString()}</span>
                          </div>
                          {/* Mini toner bars */}
                          {r.toner_k_pct != null && (
                            <div className="flex gap-1 mt-2">
                              {[
                                { v: r.toner_c_pct, c: "bg-cyan-400" },
                                { v: r.toner_m_pct, c: "bg-pink-400" },
                                { v: r.toner_y_pct, c: "bg-yellow-400" },
                                { v: r.toner_k_pct, c: "bg-gray-700" },
                              ].map((t, i) => (
                                <div key={i} className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${t.c}`} style={{ width: `${t.v ?? 0}%` }} />
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex justify-end mt-2">
                            <button onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm("이 리포트를 삭제하시겠습니까?")) {
                                api.delete(`/api/copier-reports/${r.id}`).then(() => {
                                  setCopierReports(prev => prev.filter(x => x.id !== r.id));
                                  showMsg("삭제 완료", "success");
                                }).catch(() => showMsg("삭제 실패", "error"));
                              }
                            }} className="text-[11px] text-red-400 hover:text-red-600">{t.erp_delete}</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ═══ ORDERS SECTION ═══ */}
            {section === "orders" && <CustomerOrders orders={orders} loading={ordersLoading} error={ordersError} onReload={fetchOrders} />}

            {/* ═══ CORPORATE SECTION ═══ */}
            {section === "corporate" && (
              <div className="animate-fade-in">
                <h2 className="text-[20px] font-bold text-[#1d1d1f] mb-1">{t.erp_purchaseManage}</h2>
                <p className="text-[13px] text-gray-500 mb-5">{t.erp_purchaseManageDesc}</p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Bulk Order Form */}
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <span className="text-emerald-600">{Icons.building}</span>
                      </div>
                      <div>
                        <h3 className="text-[15px] font-bold text-[#1d1d1f]">{t.erp_bulkInquiry}</h3>
                        <p className="text-[11px] text-gray-500">{t.erp_bulkInquiryDesc}</p>
                      </div>
                    </div>

                    <form onSubmit={handleBulkInquiry} className="space-y-4">
                      {bulkItems.map((item, idx) => (
                        <div key={idx} className="bg-[#f5f5f7] rounded-xl p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[12px] font-semibold text-gray-500">{t.erp_equipment} #{idx + 1}</span>
                            {bulkItems.length > 1 && (
                              <button type="button" onClick={() => setBulkItems(b => b.filter((_, i) => i !== idx))}
                                className="text-red-400 hover:text-red-600 text-[12px]">{t.erp_delete}</button>
                            )}
                          </div>
                          <input placeholder={t.erp_productReqExample} value={item.product}
                            onChange={e => setBulkItems(b => b.map((it, i) => i === idx ? { ...it, product: e.target.value } : it))}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] bg-white focus:ring-2 focus:ring-emerald-500/20 outline-none" />
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] text-gray-500">{t.erp_quantity}</label>
                              <input type="number" min="1" value={item.qty}
                                onChange={e => setBulkItems(b => b.map((it, i) => i === idx ? { ...it, qty: parseInt(e.target.value) || 1 } : it))}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] bg-white focus:ring-2 focus:ring-emerald-500/20 outline-none" />
                            </div>
                            <div>
                              <label className="text-[10px] text-gray-500">{t.erp_installLocation}</label>
                              <input placeholder={t.erp_installExample} value={item.location}
                                onChange={e => setBulkItems(b => b.map((it, i) => i === idx ? { ...it, location: e.target.value } : it))}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] bg-white focus:ring-2 focus:ring-emerald-500/20 outline-none" />
                            </div>
                          </div>
                        </div>
                      ))}

                      <button type="button" onClick={() => setBulkItems(b => [...b, { product: "", qty: 1, location: "" }])}
                        className="w-full border-2 border-dashed border-gray-300 rounded-xl py-3 text-[13px] text-gray-500 hover:border-emerald-400 hover:text-emerald-600 transition">
                        + {t.erp_addEquipment}
                      </button>

                      <button type="submit"
                        className="w-full bg-emerald-600 text-white py-3 rounded-xl text-[14px] font-bold hover:bg-emerald-700 transition shadow-sm">
                        {t.erp_sendBulkInquiry}
                      </button>
                    </form>
                  </div>

                  {/* Corporate Features Info */}
                  <div className="space-y-4">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-6">
                      <h3 className="text-[15px] font-bold text-[#1d1d1f] mb-3">{t.erp_corporateGuide}</h3>
                      <div className="space-y-3">
                        {[
                          { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>, title: t.erp_corpFeat1Title, desc: t.erp_corpFeat1Desc },
                          { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>, title: t.erp_corpFeat2Title, desc: t.erp_corpFeat2Desc },
                          { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>, title: t.erp_corpFeat3Title, desc: t.erp_corpFeat3Desc },
                          { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg>, title: t.erp_corpFeat4Title, desc: t.erp_corpFeat4Desc },
                          { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg>, title: t.erp_corpFeat5Title, desc: t.erp_corpFeat5Desc },
                        ].map((feat, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <span className="text-emerald-600 mt-0.5">{feat.icon}</span>
                            <div>
                              <p className="text-[13px] font-semibold text-[#1d1d1f]">{feat.title}</p>
                              <p className="text-[12px] text-gray-500">{feat.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-5 border border-emerald-100">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-emerald-600"><svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg></span>
                        <h3 className="text-[14px] font-bold text-[#1d1d1f]">{t.erp_dedicatedConsult}</h3>
                      </div>
                      <p className="text-[12px] text-gray-600 mb-3">{t.erp_dedicatedConsultDesc}</p>
                      <div className="flex gap-2">
                        <button onClick={() => setSection("ai")}
                          className="flex-1 bg-emerald-600 text-white py-2.5 rounded-xl text-[13px] font-semibold hover:bg-emerald-700 transition">
                          {t.erp_aiConsult}
                        </button>
                        <button onClick={() => setSection("inquiry")}
                          className="flex-1 bg-white text-emerald-700 py-2.5 rounded-xl text-[13px] font-semibold border border-emerald-200 hover:bg-emerald-50 transition">
                          {t.erp_makeInquiry}
                        </button>
                      </div>
                    </div>

                    {/* Quick links for individual buyers */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-6">
                      <h3 className="text-[15px] font-bold text-[#1d1d1f] mb-3">{t.erp_individualBuyer}</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: t.erp_aiRec, link: "/search", icon: <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg> },
                          { label: t.erp_compare, link: "/compare", icon: <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" /></svg> },
                          { label: t.erp_nationalRental, link: "/rental", icon: <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg> },
                          { label: t.erp_rentalQuote, link: "/inquiry", icon: <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
                        ].map((item, i) => (
                          <Link key={i} to={item.link}
                            className="flex items-center gap-2 bg-[#f5f5f7] rounded-xl px-4 py-3 hover:bg-gray-200 transition">
                            <span>{item.icon}</span>
                            <span className="text-[13px] font-medium text-[#1d1d1f]">{item.label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ CHAT HISTORY SECTION ═══ */}
            {section === "history" && (
              <div className="animate-fade-in">
                <h2 className="text-[20px] font-bold text-[#1d1d1f] mb-1">{t.erp_aiChatHistory}</h2>
                <p className="text-[13px] text-gray-500 mb-5">{t.erp_aiChatHistoryDesc}</p>

                {chatSessions.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100/80 p-16 text-center">
                    <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-purple-600">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" /></svg>
                    </div>
                    <p className="text-[15px] font-semibold text-[#1d1d1f] mb-1">{t.erp_noChatHistory}</p>
                    <p className="text-[13px] text-gray-500 mb-4">{t.erp_aiAsk}</p>
                    <button onClick={() => setSection("ai")} className="text-[13px] text-[#0071e3] font-semibold hover:underline">
                      {t.erp_startAiChat}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chatSessions.map(s => (
                      <button key={s.id} onClick={() => loadChatSession(s.session_id)}
                        className="w-full text-left bg-white rounded-2xl shadow-sm border border-gray-100/80 p-5 hover:shadow-md hover:border-[#0071e3]/30 transition cursor-pointer group">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-[14px] font-semibold text-[#1d1d1f] group-hover:text-[#0071e3] transition">{s.title}</p>
                              <p className="text-[11px] text-gray-400">{formatDate(s.created_at)} · {s.message_count} {t.erp_messagesCount}</p>
                            </div>
                          </div>
                          <svg className="w-4 h-4 text-gray-300 group-hover:text-[#0071e3] transition" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ═══ PROFILE SECTION ═══ */}
            {section === "profile" && (
              <div className="animate-fade-in max-w-lg">
                <CustomerProfile user={user} onSave={updateProfile} />

                {/* Account Info */}
                <div className="mt-4 bg-white rounded-2xl shadow-sm border border-gray-100/80 p-6">
                  <h3 className="text-[14px] font-bold text-[#1d1d1f] mb-3">{t.erp_accountInfo}</h3>
                  <div className="space-y-2 text-[13px]">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.erp_accountType}</span>
                      <span className="text-[#1d1d1f] font-medium">{t.erp_local} {t.erp_accountLabel}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.erp_role}</span>
                      <span className="text-[#1d1d1f] font-medium">{isAdminRole(user.role) ? roleLabel(user.role) : t.erp_roleMember}</span>
                    </div>
                  </div>
                </div>

                {/* Account Deletion */}
                <div className="mt-6 bg-red-50 rounded-2xl p-5 border border-red-100">
                  <h4 className="text-[15px] font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/></svg>
                    {t.erp_deleteAccount}
                  </h4>
                  {deleteMsg ? (
                    <p className="text-sm text-green-600">{deleteMsg}</p>
                  ) : deleteStep === 0 ? (
                    <div>
                      <p className="text-[13px] text-gray-600 mb-3">{t.erp_deleteAccountDesc}</p>
                      <button onClick={() => setDeleteStep(1)} className="text-[13px] text-red-600 hover:underline font-medium">{t.erp_deleteAccountRequest} &gt;</button>
                    </div>
                  ) : deleteStep === 1 ? (
                    <div>
                      <p className="text-[13px] text-red-600 mb-3 font-medium">{t.erp_deleteAccountConfirm}</p>
                      <input
                        type="text"
                        value={deleteInput}
                        onChange={e => setDeleteInput(e.target.value)}
                        placeholder={t.erp_deleteNow}
                        className="w-full border border-red-200 rounded-xl px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-red-200"
                      />
                      <div className="flex gap-2">
                        <button onClick={() => { setDeleteStep(0); setDeleteInput(""); }}
                          className="px-4 py-2 rounded-xl text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">{t.erp_cancel}</button>
                        <button
                          disabled={deleteInput !== (t.erp_deleteNow || "지금 삭제")}
                          onClick={async () => {
                            try {
                              await api.post("/api/users/me/delete-request", { confirm_text: deleteInput });
                              alert(t.erp_deleteAccountAlerted || "계정 삭제가 요청되었습니다. 24시간 내 재로그인하면 취소됩니다.");
                              logout();
                              navigate("/");
                            } catch (err) {
                              alert(err.response?.data?.error || t.erp_errorOccurred || "오류가 발생했습니다");
                            }
                          }}
                          className="px-4 py-2 rounded-xl text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 transition">
                          {t.erp_requestDelete}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* ═══ TRIP MANAGEMENT SECTION (출장 관리) ═══ */}
            {section === "trip" && <TripManagementSection user={user} />}

            {/* ═══ COMMUNITY SECTION ═══ */}
            {section === "community" && <MyCommunitySection user={user} />}

          </main>
        </div>
      </div>
    </>
  );
}


/* ═══════════════════════════════════════════════════════════
   MY COMMUNITY SECTION (내 커뮤니티 활동)
   ═══════════════════════════════════════════════════════════ */
function MyCommunitySection({ user }) {
  const { t } = useI18n();
  const [subTab, setSubTab] = useState("posts");
  const [myPosts, setMyPosts] = useState([]);
  const [myComments, setMyComments] = useState([]);
  const [myPoints, setMyPoints] = useState(null);
  const [pointHistory, setPointHistory] = useState([]);
  const navigate = useNavigate();

  const BADGE_NAMES = { 1: t.erp_badgeA4 || "A4용지", 2: t.erp_badgeInkjet || "잉크젯", 3: t.erp_badgeColorLaser || "컬러레이저", 4: t.erp_badgeMaster || "마스터엔지니어" };
  const BADGE_COLORS = { 1: "bg-gray-100 text-gray-500", 2: "bg-green-100 text-green-600", 3: "bg-blue-100 text-blue-600", 4: "bg-purple-100 text-purple-600" };

  useEffect(() => {
    if (subTab === "posts") api.get("/api/community/my/posts").then(r => setMyPosts(r.data.posts || [])).catch(() => {});
    else if (subTab === "comments") api.get("/api/community/my/comments").then(r => setMyComments(r.data.comments || [])).catch(() => {});
    else if (subTab === "points") {
      api.get("/api/points/my").then(r => setMyPoints(r.data)).catch(() => {});
      api.get("/api/points/history").then(r => setPointHistory(r.data.history || [])).catch(() => {});
    }
  }, [subTab]);

  return (
    <div className="animate-fade-in">
      <h2 className="text-[20px] font-bold text-[#1d1d1f] mb-1">{t.erp_myReviewLounge}</h2>
      <p className="text-[13px] text-gray-500 mb-5">{t.erp_communityDesc}</p>

      {/* Points summary card */}
      <div className="bg-gradient-to-r from-blue-50 to-white rounded-2xl border border-blue-100 p-5 mb-5 flex items-center gap-5">
        <div>
          <p className="text-[11px] text-[#86868b]">{t.erp_myTonerPoints}</p>
          <p className="text-3xl font-bold text-[#0071e3]">{user.toner_points || 0}<span className="text-sm font-normal text-[#86868b] ml-1">P</span></p>
        </div>
        <div className="h-10 w-px bg-gray-200" />
        <div>
          <p className="text-[11px] text-[#86868b]">{t.erp_myBadge}</p>
          <span className={`inline-block mt-1 text-xs px-2.5 py-1 rounded-full font-medium ${BADGE_COLORS[user.badge_level || 1]}`}>
            {BADGE_NAMES[user.badge_level || 1]}
          </span>
        </div>
        <button onClick={() => navigate("/community")} className="ml-auto px-4 py-2 rounded-xl bg-[#0071e3] text-white text-sm font-medium hover:bg-[#0077ed] transition">
          {t.erp_goToReviewLounge}
        </button>
      </div>

      {/* Sub tabs */}
      <div className="flex gap-2 bg-gray-100/80 rounded-xl p-1 w-fit mb-4">
        {[{ k: "posts", l: t.erp_myPosts }, { k: "comments", l: t.erp_myComments }, { k: "points", l: t.erp_pointHistory }].map(t2 => (
          <button key={t2.k} onClick={() => setSubTab(t2.k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${subTab === t2.k ? "bg-white text-[#1d1d1f] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>{t2.l}</button>
        ))}
      </div>

      {/* My posts */}
      {subTab === "posts" && (
        <div className="space-y-2">
          {myPosts.length === 0 ? <p className="text-sm text-[#86868b] py-10 text-center">{t.erp_noMyPosts}</p> : myPosts.map(p => (
            <div key={p.id} onClick={() => navigate(`/community?post=${p.id}`)}
              className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition cursor-pointer">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-[#6e6e73]">{p.category_name}</span>
                <span className="text-[10px] text-[#86868b]">{new Date(p.created_at).toLocaleDateString("ko")}</span>
              </div>
              <h4 className="text-sm font-semibold text-[#1d1d1f]">{p.title}</h4>
              <div className="flex items-center gap-3 mt-1 text-[11px] text-[#86868b]">
                <span className="text-[10px] text-[#86868b]">{t.erp_rec} {p.upvotes}</span>
                <span>{t.erp_commentsLabel} {p.comment_count}</span>
                <span>{t.erp_views} {p.view_count}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* My comments */}
      {subTab === "comments" && (
        <div className="space-y-2">
          {myComments.length === 0 ? <p className="text-sm text-[#86868b] py-10 text-center">{t.erp_noMyComments}</p> : myComments.map(c => (
            <div key={c.id} onClick={() => navigate(`/community?post=${c.post_id}`)}
              className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition cursor-pointer">
              <p className="text-sm text-[#1d1d1f]">{c.content}</p>
              <div className="flex items-center gap-2 mt-1 text-[10px] text-[#86868b]">
                <span className="font-medium">{c.post_title}</span>
                <span>{new Date(c.created_at).toLocaleDateString("ko")}</span>
                <span>{t.erp_rec} {c.upvotes}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Points history */}
      {subTab === "points" && (
        <div>
          {myPoints && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
                <p className="text-[11px] text-[#86868b]">{t.erp_totalPoints}</p>
                <p className="text-xl font-bold text-[#0071e3]">{myPoints.toner_points || 0}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
                <p className="text-[11px] text-[#86868b]">{t.erp_badgeLevel}</p>
                <p className="text-xl font-bold">{BADGE_NAMES[myPoints.badge_level || 1]}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
                <p className="text-[11px] text-[#86868b]">{t.erp_maxDiscount}</p>
                <p className="text-xl font-bold text-green-600">1%</p>
              </div>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full text-[12px]">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">{t.erp_typeLabel}</th>
                  <th className="px-3 py-2 text-center">{t.erp_pointLabel}</th>
                  <th className="px-3 py-2 text-left">{t.erp_reason}</th>
                  <th className="px-3 py-2 text-left">{t.erp_dateTime}</th>
                </tr>
              </thead>
              <tbody>
                {pointHistory.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-[#86868b]">{t.erp_noPointHistory}</td></tr>
                ) : pointHistory.map((h, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${h.type === "earn" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>{h.type === "earn" ? t.erp_earn : t.erp_spend}</span></td>
                    <td className={`px-3 py-2 text-center font-mono font-semibold ${h.type === "earn" ? "text-green-600" : "text-red-600"}`}>{h.type === "earn" ? "+" : "-"}{h.amount}</td>
                    <td className="px-3 py-2 text-gray-500">{h.reason}</td>
                    <td className="px-3 py-2 text-gray-400">{new Date(h.created_at).toLocaleString("ko")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   TRIP MANAGEMENT SECTION (출장 관리 – 내비게이션)
   ═══════════════════════════════════════════════════════════ */
const VWORLD_KEY = 'CFDB3172-C1F9-3E4C-9E65-7D391C6F4548';
const VWORLD_TILE = `https://api.vworld.kr/req/wmts/1.0.0/${VWORLD_KEY}/Base/{z}/{y}/{x}.png`;

/* 주요 지역 좌표 (VWorld / Kakao 기반) */
const LOCATION_DB = {
  '서울': [37.5665, 126.978], '강남': [37.4979, 127.0276], '종로': [37.5704, 126.9831],
  '영등포': [37.5264, 126.8963], '마포': [37.5663, 126.9014], '송파': [37.5145, 127.1059],
  '인천': [37.4563, 126.7052], '수원': [37.2636, 127.0286], '성남': [37.4200, 127.1267],
  '용인': [37.2411, 127.1776], '고양': [37.6584, 126.832], '부천': [37.5034, 126.766],
  '안양': [37.3943, 126.9568], '파주': [37.7599, 126.78], '화성': [37.1996, 126.831],
  '안산': [37.3219, 126.8309], '시흥': [37.3801, 126.8034], '평택': [36.992, 127.112],
  '부산': [35.1796, 129.0756], '해운대': [35.1631, 129.1635], '대구': [35.8714, 128.6014],
  '광주': [35.1595, 126.8526], '대전': [36.3504, 127.3845], '울산': [35.5384, 129.3114],
  '세종': [36.48, 127.26], '제주': [33.4996, 126.5312],
  '천안': [36.8151, 127.1139], '청주': [36.6424, 127.489], '전주': [35.8242, 127.148],
  '포항': [36.019, 129.3435], '창원': [35.2281, 128.6812], '김해': [35.2285, 128.8894],
  '원주': [37.3422, 127.9202], '춘천': [37.8813, 127.73], '강릉': [37.7519, 128.8761],
  '여수': [34.7604, 127.6622], '순천': [34.9506, 127.4873], '목포': [34.8118, 126.3922],
  '경주': [35.8562, 129.2247], '구미': [36.1195, 128.3446], '진주': [35.18, 128.1076],
  '익산': [35.9483, 126.9577], '군산': [35.9679, 126.7364],
  // 서울 구
  '서초': [37.4837, 127.0324], '강서': [37.5509, 126.8497], '구로': [37.4954, 126.8874],
  '관악': [37.4784, 126.9516], '동작': [37.5124, 126.9393], '성동': [37.5633, 127.0371],
  '광진': [37.5385, 127.0823], '중랑': [37.6063, 127.0928], '노원': [37.6542, 127.0568],
  '도봉': [37.6688, 127.0472], '강북': [37.6397, 127.0255], '성북': [37.5894, 127.0167],
  '은평': [37.6027, 126.9291], '서대문': [37.5791, 126.9368], '용산': [37.5324, 126.9908],
  '중구': [37.5641, 126.9979], '동대문': [37.5744, 127.0396],
};

function geocodeLocal(text) {
  if (!text) return null;
  for (const [name, coords] of Object.entries(LOCATION_DB)) {
    if (text.includes(name)) return { name, lat: coords[0], lng: coords[1] };
  }
  return null;
}

function TripManagementSection({ user }) {
  const { t } = useI18n();
  const [subTab, setSubTab] = useState('plan'); // plan | history
  const [trips, setTrips] = useState([]);
  const [showMap, setShowMap] = useState(true);

  // Trip form
  const [departure, setDeparture] = useState('');
  const [destination, setDestination] = useState('');
  const [waypoints, setWaypoints] = useState(['']);
  const [tripDate, setTripDate] = useState(new Date().toISOString().slice(0, 10));
  const [tripTime, setTripTime] = useState('09:00');
  const [tripMemo, setTripMemo] = useState('');
  const [tripType, setTripType] = useState('install'); // install | inspect | pickup | delivery
  const navigate = useNavigate();

  // Load saved trips from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`ik_trips_${user?.id || 'guest'}`);
      if (saved) setTrips(JSON.parse(saved));
    } catch {}
  }, [user]);

  const saveTrips = (updated) => {
    setTrips(updated);
    try { localStorage.setItem(`ik_trips_${user?.id || 'guest'}`, JSON.stringify(updated)); } catch {}
  };

  const addWaypoint = () => { if (waypoints.length < 5) setWaypoints([...waypoints, '']); };
  const removeWaypoint = (i) => setWaypoints(waypoints.filter((_, idx) => idx !== i));
  const updateWaypoint = (i, v) => { const w = [...waypoints]; w[i] = v; setWaypoints(w); };

  // Geocode all points for map
  const allPoints = [
    { label: '출발', geo: geocodeLocal(departure), text: departure },
    ...waypoints.filter(w => w.trim()).map((w, i) => ({ label: `경유${i + 1}`, geo: geocodeLocal(w), text: w })),
    { label: '도착', geo: geocodeLocal(destination), text: destination },
  ].filter(p => p.geo);

  const mapCenter = allPoints.length > 0
    ? [allPoints.reduce((s, p) => s + p.geo.lat, 0) / allPoints.length, allPoints.reduce((s, p) => s + p.geo.lng, 0) / allPoints.length]
    : [37.5665, 126.978];
  const mapZoom = allPoints.length <= 1 ? 12 : allPoints.length <= 3 ? 9 : 7;

  const typeLabels = { install: '설치', inspect: '점검', pickup: '수거', delivery: '납품' };
  const typeColors = { install: 'bg-blue-100 text-blue-700', inspect: 'bg-green-100 text-green-700', pickup: 'bg-orange-100 text-orange-700', delivery: 'bg-purple-100 text-purple-700' };
  const pointColors = { '출발': '#0071e3', '도착': '#ef4444' };

  const handleSaveTrip = () => {
    if (!departure.trim() || !destination.trim()) return;
    const trip = {
      id: Date.now(),
      departure, destination,
      waypoints: waypoints.filter(w => w.trim()),
      date: tripDate, time: tripTime,
      type: tripType, memo: tripMemo,
      status: 'planned', // planned | in_progress | completed | cancelled
      createdAt: new Date().toISOString(),
      points: allPoints,
    };
    const updated = [trip, ...trips];
    saveTrips(updated);
    // Reset form
    setDeparture(''); setDestination(''); setWaypoints(['']); setTripMemo('');
    setTripType('install'); setTripTime('09:00'); setTripDate(new Date().toISOString().slice(0, 10));
    setSubTab('history');
  };

  const updateTripStatus = (id, status) => {
    saveTrips(trips.map(tr => tr.id === id ? { ...tr, status } : tr));
  };

  const deleteTrip = (id) => {
    saveTrips(trips.filter(tr => tr.id !== id));
  };

  const statusLabels = { planned: '예정', in_progress: '이동 중', completed: '완료', cancelled: '취소' };
  const statusColors = { planned: 'bg-blue-100 text-blue-700', in_progress: 'bg-yellow-100 text-yellow-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-gray-100 text-gray-500' };

  let MapContainer, TileLayer, CircleMarker, Popup, Polyline;
  try {
    const rl = require('react-leaflet');
    MapContainer = rl.MapContainer;
    TileLayer = rl.TileLayer;
    CircleMarker = rl.CircleMarker;
    Popup = rl.Popup;
    Polyline = rl.Polyline;
    require('leaflet/dist/leaflet.css');
  } catch { /* leaflet not available */ }

  return (
    <div className="animate-fade-in">
      <h2 className="text-[20px] font-bold text-[#1d1d1f] mb-1">🚐 {t.erp_tripManage || '출장 관리'}</h2>
      <p className="text-[13px] text-gray-500 mb-5">{t.erp_tripDesc || '출장 경로를 계획하고, 출발·경유·도착지를 관리하세요'}</p>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-5">
        {[{ key: 'plan', label: '📝 출장 계획' }, { key: 'history', label: `📋 출장 내역 (${trips.length})` }].map(tab => (
          <button key={tab.key} onClick={() => setSubTab(tab.key)}
            className={`px-4 py-2 rounded-xl text-[13px] font-medium transition ${subTab === tab.key ? 'bg-[#1d1d1f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── PLAN TAB ── */}
      {subTab === 'plan' && (
        <div className="space-y-5">
          {/* Trip Type & Date */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <p className="text-[14px] font-bold text-[#1d1d1f] mb-3">출장 정보</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(typeLabels).map(([k, v]) => (
                <button key={k} onClick={() => setTripType(k)}
                  className={`px-3 py-2.5 rounded-xl text-[12px] font-medium border transition ${tripType === k ? 'bg-[#0071e3] text-white border-[#0071e3]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                  {k === 'install' ? '🔧' : k === 'inspect' ? '🔍' : k === 'pickup' ? '📦' : '🚚'} {v}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[11px] text-gray-500 mb-1 block">출장 날짜</label>
                <input type="date" value={tripDate} onChange={e => setTripDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 mb-1 block">출발 시간</label>
                <input type="time" value={tripTime} onChange={e => setTripTime(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30" />
              </div>
            </div>
          </div>

          {/* Route Planning */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <p className="text-[14px] font-bold text-[#1d1d1f] mb-3">🗺️ 경로 설정</p>

            {/* Departure */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-[#0071e3] text-white flex items-center justify-center text-[11px] font-bold shrink-0">출발</div>
              <input value={departure} onChange={e => setDeparture(e.target.value)} placeholder="출발지 (예: 서울 영등포, 강남구 등)"
                className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30" />
            </div>

            {/* Waypoints */}
            {waypoints.map((wp, i) => (
              <div key={i} className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold shrink-0">경유</div>
                <input value={wp} onChange={e => updateWaypoint(i, e.target.value)} placeholder={`경유지 ${i + 1} (선택사항)`}
                  className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                <button onClick={() => removeWaypoint(i)} className="text-gray-400 hover:text-red-500 transition">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ))}
            {waypoints.length < 5 && (
              <button onClick={addWaypoint} className="text-[12px] text-[#0071e3] font-medium hover:underline mb-3">
                + 경유지 추가
              </button>
            )}

            {/* Destination */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-[11px] font-bold shrink-0">도착</div>
              <input value={destination} onChange={e => setDestination(e.target.value)} placeholder="도착지 (예: 부산 해운대, 대전 등)"
                className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-red-500/30" />
            </div>
          </div>

          {/* VWorld Map Preview */}
          {MapContainer && allPoints.length >= 1 && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-5 pt-4 pb-2">
                <p className="text-[13px] font-semibold text-gray-700">📍 경로 미리보기</p>
                <button onClick={() => setShowMap(!showMap)} className="text-[11px] text-[#0071e3] hover:underline">{showMap ? '접기' : '펼치기'}</button>
              </div>
              {showMap && (
                <div style={{ height: 320 }}>
                  <MapContainer key={`${mapCenter[0]}-${mapCenter[1]}-${mapZoom}`} center={mapCenter} zoom={mapZoom} style={{ width: '100%', height: '100%' }} zoomControl={false}>
                    <TileLayer url={VWORLD_TILE} maxZoom={19} attribution="© VWorld" />
                    {/* Route line */}
                    {allPoints.length >= 2 && (
                      <Polyline positions={allPoints.map(p => [p.geo.lat, p.geo.lng])} pathOptions={{ color: '#0071e3', weight: 3, dashArray: '8 6', opacity: 0.7 }} />
                    )}
                    {/* Points */}
                    {allPoints.map((p, i) => (
                      <CircleMarker key={i} center={[p.geo.lat, p.geo.lng]}
                        radius={p.label === '출발' || p.label === '도착' ? 10 : 7}
                        pathOptions={{ fillColor: pointColors[p.label] || '#f59e0b', color: '#fff', weight: 2, fillOpacity: 0.9 }}>
                        <Popup>
                          <div style={{ fontSize: 12, minWidth: 100 }}>
                            <p style={{ fontWeight: 700, color: pointColors[p.label] || '#f59e0b' }}>{p.label}</p>
                            <p style={{ color: '#666' }}>{p.text}</p>
                          </div>
                        </Popup>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                </div>
              )}
              <div className="px-5 py-2 text-[10px] text-gray-400 text-right">VWorld 공간정보 기반</div>
            </div>
          )}

          {/* Memo */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <label className="text-[12px] text-gray-500 mb-1 block">메모 (선택)</label>
            <textarea value={tripMemo} onChange={e => setTripMemo(e.target.value)} rows={2} placeholder="고객명, 기기모델, 특이사항 등"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30" />
          </div>

          {/* Save button */}
          <button onClick={handleSaveTrip} disabled={!departure.trim() || !destination.trim()}
            className="w-full py-3.5 rounded-2xl bg-[#0071e3] text-white text-[14px] font-semibold hover:bg-[#0077ed] disabled:bg-gray-200 disabled:text-gray-400 transition shadow-sm">
            🚐 출장 등록하기
          </button>
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {subTab === 'history' && (
        <div className="space-y-3">
          {trips.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-4xl mb-3">🚐</p>
              <p className="text-[14px] font-medium text-gray-500">아직 등록된 출장이 없습니다</p>
              <button onClick={() => setSubTab('plan')} className="mt-3 px-4 py-2 rounded-xl bg-[#0071e3] text-white text-[13px] font-medium">출장 계획하기</button>
            </div>
          ) : trips.map(trip => (
            <div key={trip.id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${typeColors[trip.type]}`}>{typeLabels[trip.type]}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[trip.status]}`}>{statusLabels[trip.status]}</span>
                </div>
                <span className="text-[11px] text-gray-400">{trip.date} {trip.time}</span>
              </div>

              {/* Route visualization */}
              <div className="flex items-center gap-2 text-[13px] mb-2">
                <span className="font-semibold text-[#0071e3]">{trip.departure}</span>
                {trip.waypoints?.length > 0 && trip.waypoints.map((w, i) => (
                  <React.Fragment key={i}>
                    <span className="text-gray-300">→</span>
                    <span className="text-amber-600">{w}</span>
                  </React.Fragment>
                ))}
                <span className="text-gray-300">→</span>
                <span className="font-semibold text-red-500">{trip.destination}</span>
              </div>

              {trip.memo && <p className="text-[11px] text-gray-500 mb-2">💬 {trip.memo}</p>}

              {/* Mini map for saved trip */}
              {MapContainer && trip.points?.length >= 2 && (
                <div className="rounded-xl overflow-hidden border border-gray-100 mb-2" style={{ height: 150 }}>
                  <MapContainer
                    center={[trip.points.reduce((s, p) => s + p.geo.lat, 0) / trip.points.length, trip.points.reduce((s, p) => s + p.geo.lng, 0) / trip.points.length]}
                    zoom={trip.points.length <= 2 ? 9 : 7} style={{ width: '100%', height: '100%' }} zoomControl={false} dragging={false} scrollWheelZoom={false}>
                    <TileLayer url={VWORLD_TILE} maxZoom={19} />
                    <Polyline positions={trip.points.map(p => [p.geo.lat, p.geo.lng])} pathOptions={{ color: '#0071e3', weight: 2, dashArray: '6 4', opacity: 0.6 }} />
                    {trip.points.map((p, i) => (
                      <CircleMarker key={i} center={[p.geo.lat, p.geo.lng]} radius={6}
                        pathOptions={{ fillColor: pointColors[p.label] || '#f59e0b', color: '#fff', weight: 1.5, fillOpacity: 0.85 }} />
                    ))}
                  </MapContainer>
                </div>
              )}

              {/* Status actions */}
              <div className="flex gap-2 mt-1">
                {trip.status === 'planned' && (
                  <>
                    <button onClick={() => updateTripStatus(trip.id, 'in_progress')} className="px-3 py-1.5 rounded-lg text-[11px] font-medium bg-yellow-50 text-yellow-700 hover:bg-yellow-100 transition">🚗 출발</button>
                    <button onClick={() => updateTripStatus(trip.id, 'cancelled')} className="px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gray-50 text-gray-500 hover:bg-gray-100 transition">취소</button>
                  </>
                )}
                {trip.status === 'in_progress' && (
                  <button onClick={() => updateTripStatus(trip.id, 'completed')} className="px-3 py-1.5 rounded-lg text-[11px] font-medium bg-green-50 text-green-700 hover:bg-green-100 transition">✅ 완료</button>
                )}
                <button onClick={() => deleteTrip(trip.id)} className="ml-auto px-3 py-1.5 rounded-lg text-[11px] font-medium text-red-400 hover:bg-red-50 transition">삭제</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
